import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Grid,
  Button,
  InputLabel,
  Box,
  Chip,
  FormControl,
  NativeSelect,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DoneIcon from "@mui/icons-material/Done";
import CancelIcon from "@mui/icons-material/Cancel";
import CreateIcon from "@mui/icons-material/Create";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  getAllVehicle,
  getAllVehicleByMenu,
  createVehicle,
  removeVehicle,
  deleteVehicle,
  updateVehicle,
} from "../../actions/VehicleInfoAction";
import DialogVehicleModel from "../Model/DialogVehicleModel";
import keycloakService from "../../keycloakService";
import { ROLE } from "../../utils/helper";
import { styled } from "@mui/material/styles";
import AlertSnackBar from "../Model/AlertSnackBar";
import CircularProgress from "@mui/material/CircularProgress";
import Menus from "../TableMenu/Menus";
import { FilterMenu } from "../../utils/enum";

const StyledDiv = styled("div")(({ theme }) => ({
  "& .table": {
    minWidth: 650,
  },
  "& .addIcon": {
    color: "#05234A",
    border: "solid 1px #05234A",
    padding: "2px",
    margin: "5px",
    borderRadius: "100%",
  },
  "& .clickedAddIcon": {
    color: "white",
    backgroundColor: "#05234A",
    padding: "2px",
    margin: "5px",
    borderRadius: "100%",
  },
  "& .addButton": {
    color: "white",
    backgroundColor: "#4caf50",
    padding: "2px",
    margin: "5px",
    borderRadius: "100%",
  },
  "& .addButtonDisable": {
    color: "white",
    backgroundColor: "gray",
    padding: "2px",
    margin: "5px",
    borderRadius: "100%",
  },
  "& .addButtonAdd": {
    color: "white",
    backgroundColor: "#4caf50",
    padding: "10px",
    margin: "10px",
    borderRadius: "100%",
    cursor: "pointer",
  },
  "& .selectedMenu": {
    backgroundColor: "#05234A",
    color: "white",
    "&:hover": {
      backgroundColor: "#05234A",
    },
  },
}));

const dropDownLimit = 8;

function AddVehicle() {
  const [showSearch, setShowSearch] = useState(false);
  const [vehicleList, setVehicleList] = useState([]);
  const [begin, setBegin] = useState(0);
  const [end, setEnd] = useState(0);
  const [sliceList, setSliceList] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState("all");
  const [newArrayList, setNewArrayList] = useState(0);
  const [vehicleType, setVehicleType] = useState("");
  const [fuelCost, setFuelCost] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const hasSuperAdmin = keycloakService.hasRole([ROLE.SuperAdmin]);
  const [showEdit, setShowEdit] = useState(false);
  const [needEdit, setNeedEdit] = useState("");
  const [newEditValues, setNewEditValues] = useState({
    id: "",
    name: "",
    status: "",
    fuel: 0,
    available: "",
  });
  const [lineProgress, setLineProgress] = useState(false);
  const [snackState, setSnackState] = useState({
    open: false,
    alertType: "",
    message: "",
  });

  const reFetchingVehicle = async (newMenuState = null) => {
    setLineProgress(true);

    const menuToUse = newMenuState || selectedMenu;

    const routePointList = getAllVehicleByMenu(FilterMenu[menuToUse], (res) => {
      setVehicleList(res);
      setLineProgress(false);
    });
    await routePointList();
  };

  useEffect(() => {
    setLineProgress(true);
    const fetchingFunction = async () => {
      const routePointList = getAllVehicleByMenu(
        FilterMenu[selectedMenu],
        (res) => {
          setVehicleList(res);
          setLineProgress(false);
        }
      );
      await routePointList();
    };
    fetchingFunction();
  }, [selectedMenu]);

  useEffect(() => {
    const statusMap = {
      all: null,
      published: "PUBLISHED",
      pen_publish: "PENDING_PUBLISH",
      pen_delete: "PENDING_DELETION",
      delete: "DELETED",
    };

    let filteredList = vehicleList;
    if (statusMap[selectedMenu]) {
      filteredList = vehicleList.filter(
        (item) => item.vehicleStatus === statusMap[selectedMenu]
      );
    }

    const totalCount = filteredList.length;
    setNewArrayList(totalCount);

    if (begin === 0 && end === 0) {
      const itemsPerPage = Math.min(dropDownLimit, totalCount);
      setEnd(itemsPerPage);
      setSliceList(filteredList.slice(0, itemsPerPage));
    } else {
      setSliceList(filteredList.slice(begin, end));
    }
  }, [vehicleList, begin, end, selectedMenu]);

  useEffect(() => {
    setBegin(0);
    setEnd(0);
  }, [selectedMenu]);

  const handleState = (status) => {
    setBegin(0);
    setEnd(0);
    setSelectedMenu(status);
  };

  const handleBack = () => {
    if (begin === 0) return;
    const newBegin = Math.max(0, begin - dropDownLimit);
    setBegin(newBegin);
    setEnd(Math.min(newBegin + dropDownLimit, vehicleList.length));
  };

  const handleForward = () => {
    if (end >= newArrayList) return;
    const newBegin = begin + dropDownLimit;
    const newEnd = Math.min(newBegin + dropDownLimit, vehicleList.length);
    setBegin(newBegin);
    setEnd(newEnd);
  };

  const correctColorForStatus = (status) => {
    switch (status) {
      case "PUBLISHED":
        return "#35AA2B";
      case "DELETED":
        return "#EC6767";
      case "PENDING_PUBLISH":
        return "black";
      default:
        return "orange";
    }
  };

  const correctColorForAvailability = (availability) => {
    if (availability === true || availability === "true") {
      return "primary";
    } else {
      return "secondary";
    }
  };

  const validateInputData = (value) => {
    if (value > 0) {
      return true;
    } else {
      setSnackState({
        open: true,
        alertType: "error",
        message: "Please enter a positive value for Fuel km/litre",
      });
      return false;
    }
  };

  const addingVehicle = async () => {
    if (!validateInputData(fuelCost)) return;

    const vehicle = {
      vehicleType: vehicleType,
      fuelKmPerLitter: fuelCost,
    };

    setLineProgress(true);

    const newMenu = hasSuperAdmin ? "published" : "pen_publish";

    const createNewVehicle = createVehicle(vehicle, async () => {
      await reFetchingVehicle(newMenu);
      setSelectedMenu(newMenu);
    });

    await createNewVehicle();

    setShowSearch(false);
    setVehicleType("");
    setFuelCost(0);
  };

  const handleDelete = async (id) => {
    if (!id) return;

    setLineProgress(true);

    // Determine which menu to select after deletion
    const newMenu = hasSuperAdmin ? "delete" : "pen_delete";

    if (hasSuperAdmin) {
      const deleted = deleteVehicle(id, async () => {
        await reFetchingVehicle(newMenu);
        setSelectedMenu(newMenu);
      });
      await deleted();
    } else {
      const remove = removeVehicle(id, async () => {
        await reFetchingVehicle(newMenu);
        setSelectedMenu(newMenu);
      });
      await remove();
    }

    setOpen(false);
  };

  const handleEdit = (edit) => {
    setNewEditValues({
      id: edit.id,
      name: edit.vehicleType,
      status: edit.vehicleStatus,
      fuel: edit.fuelKmPerLitter,
      available: edit.availability,
    });
    setShowEdit(true);
    setNeedEdit(edit.id);
  };

  const handleUpdate = async () => {
    if (!newEditValues || !validateInputData(newEditValues.fuel)) return;

    const vehicle = {
      id: newEditValues.id,
      vehicleType: newEditValues.name,
      fuelKmPerLitter: newEditValues.fuel,
      availability: newEditValues.available,
      vehicleStatus: newEditValues.status,
    };

    setLineProgress(true);

    let newMenu;
    switch (newEditValues.status) {
      case "PUBLISHED":
        newMenu = "published";
        break;
      case "PENDING_PUBLISH":
        newMenu = "pen_publish";
        break;
      case "DELETED":
        newMenu = "delete";
        break;
      default:
        newMenu = selectedMenu;
        break;
    }

    const update = updateVehicle(vehicle, async () => {
      await reFetchingVehicle(newMenu);
      setSelectedMenu(newMenu);
    });

    await update();

    setShowEdit(false);
    setNeedEdit("");
  };

  const handleCloseEdit = () => {
    setShowEdit(false);
    setNeedEdit("");
  };
  const handleValidation = () => {
    if (
      (newEditValues.status === "PENDING_PUBLISH" ||
        newEditValues.status === "DELETED") &&
      newEditValues.available === "true"
    ) {
      return true;
    } else {
      return false;
    }
  };
  console.log(newEditValues);
  return (
    <StyledDiv>
      <Card style={{ border: "#083979 1px solid", borderRadius: "15px" }}>
        <CardContent>
          <Grid
            container
            spacing={1}
            style={{ justifyContent: "space-between" }}
          >
            <Menus selectedMenu={selectedMenu} handleState={handleState} />
            <Grid item style={{ display: "flex" }}>
              <Grid
                item
                style={{
                  display: showSearch ? "block" : "none",
                  marginRight: "10px",
                }}
              >
                <TextField
                  id="vehicle_type"
                  label="Vehicle Name & Num"
                  variant="outlined"
                  required
                  size="small"
                  style={{ margin: "5px" }}
                  onChange={(e) => setVehicleType(e.target.value)}
                  value={vehicleType}
                />
                <input
                  type="number"
                  size="small"
                  style={{ margin: "5px", padding: "10px" }}
                  placeholder="Fuel km/litre"
                  required
                  onChange={(e) => setFuelCost(e.target.value)}
                  value={fuelCost || ""}
                />
              </Grid>
              <Grid
                style={{
                  marginLeft: "110px",
                  marginTop: "5px",
                  display: "flex",
                }}
              >
                <Grid
                  item
                  style={{
                    display: vehicleType && fuelCost ? "block" : "none",
                    marginTop: "10px",
                  }}
                  onClick={addingVehicle}
                >
                  <label className="addButtonAdd">Add</label>
                </Grid>
                <Grid item>
                  <AddIcon
                    fontSize="large"
                    style={{
                      display: vehicleType && fuelCost ? "none" : "block",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowSearch(!showSearch)}
                    className={showSearch ? "clickedAddIcon" : "addIcon"}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <TableContainer component={Paper}>
          <Table className="table" aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell>Vehicle ID & Name</TableCell>
                <TableCell>Publish Status</TableCell>
                <TableCell>Fuel km/litre</TableCell>
                <TableCell>Vehicle Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lineProgress && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              )}
              {!lineProgress && sliceList.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    No vehicles found
                  </TableCell>
                </TableRow>
              )}
              {!lineProgress &&
                sliceList.length > 0 &&
                sliceList.map((row) =>
                  showEdit && needEdit === row.id ? (
                    <TableRow key={row.id}>
                      <TableCell>
                        <TextField
                          id="standard-basic"
                          label="Vehicle Name & Type"
                          variant="standard"
                          value={newEditValues.name}
                          onChange={(e) =>
                            setNewEditValues((prevValue) => ({
                              ...prevValue,
                              name: e.target.value,
                            }))
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 120 }}>
                          <FormControl>
                            <InputLabel
                              variant="standard"
                              htmlFor="uncontrolled-native"
                            >
                              Vehicle Status
                            </InputLabel>
                            <NativeSelect
                              value={newEditValues.status}
                              inputProps={{
                                name: "status",
                                id: "uncontrolled-native",
                              }}
                              onChange={(e) =>
                                setNewEditValues((prevValue) => ({
                                  ...prevValue,
                                  status: e.target.value,
                                }))
                              }
                            >
                              <option value="PENDING_PUBLISH">
                                PENDING PUBLISH
                              </option>
                              <option value="PUBLISHED">PUBLISHED</option>
                              <option value="DELETED">DELETED</option>
                            </NativeSelect>
                          </FormControl>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <input
                          type="number"
                          size="small"
                          style={{ margin: "5px", padding: "10px" }}
                          placeholder="Fuel km/litre"
                          required
                          value={newEditValues.fuel}
                          onChange={(e) =>
                            setNewEditValues((prevValue) => ({
                              ...prevValue,
                              fuel: e.target.value,
                            }))
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ minWidth: 120 }}>
                          <FormControl>
                            <InputLabel
                              variant="standard"
                              htmlFor="uncontrolled-native-availability"
                            >
                              Availability
                            </InputLabel>
                            <NativeSelect
                              value={newEditValues.available}
                              inputProps={{
                                name: "availability",
                                id: "uncontrolled-native-availability",
                              }}
                              onChange={(e) =>
                                setNewEditValues((prevValue) => ({
                                  ...prevValue,
                                  available: e.target.value,
                                }))
                              }
                            >
                              <option value="true">Available</option>
                              <option value="false">Not Available</option>
                            </NativeSelect>
                          </FormControl>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Button
                          style={{ color: "#E1BE43", borderRadius: "100%" }}
                          onClick={handleUpdate}
                          disabled={handleValidation()}
                        >
                          <DoneIcon
                            fontSize="large"
                            className={
                              handleValidation()
                                ? "addButtonDisable"
                                : "addButton"
                            }
                          />
                        </Button>
                        <Button
                          style={{ color: "#E1BE43", borderRadius: "100%" }}
                          onClick={handleCloseEdit}
                        >
                          <CancelIcon fontSize="large" />
                        </Button>
                        <Button
                          style={{ color: "#EC6767", borderRadius: "100%" }}
                          onClick={() => {
                            setSelectedVehicle(row);
                            setOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow key={row.id}>
                      <TableCell>{row.vehicleType}</TableCell>
                      <TableCell
                        style={{
                          color: correctColorForStatus(row.vehicleStatus),
                        }}
                      >
                        {row.vehicleStatus}
                      </TableCell>
                      <TableCell
                        style={{
                          color: "#35AA2B",
                        }}
                      >
                        {row.fuelKmPerLitter}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={
                            row.availability ? "Available" : "Not Available"
                          }
                          color={correctColorForAvailability(row.availability)}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {hasSuperAdmin && (
                          <Button
                            style={{ color: "#E1BE43", borderRadius: "100%" }}
                            onClick={() => handleEdit(row)}
                          >
                            <CreateIcon />
                          </Button>
                        )}
                        <Button
                          style={{ color: "#EC6767", borderRadius: "100%" }}
                          onClick={() => {
                            setSelectedVehicle(row);
                            setOpen(true);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          display="flex"
          justifyContent="center"
          mt={3}
          style={{ margin: "10px" }}
        >
          <Grid container justifyContent="center" alignItems="center">
            <Button
              disabled={begin === 0}
              style={{
                border: "1px solid #05234A",
                borderRadius: "10px",
                margin: "2px",
              }}
              onClick={handleBack}
            >
              <ArrowBackIosIcon />
            </Button>
            <Button
              disabled={end >= newArrayList}
              style={{
                border: "1px solid #05234A",
                borderRadius: "10px",
                margin: "2px",
              }}
              onClick={handleForward}
            >
              <ArrowForwardIosIcon />
            </Button>
          </Grid>
        </Box>
      </Card>
      <DialogVehicleModel
        vehicle={selectedVehicle}
        open={open}
        handleClose={() => setOpen(false)}
        handleAgree={() => handleDelete(selectedVehicle?.id)}
      />
      <AlertSnackBar
        open={snackState.open}
        handleClose={() =>
          setSnackState((prevValue) => ({ ...prevValue, open: false }))
        }
        alertType={snackState.alertType}
        message={snackState.message}
      />
    </StyledDiv>
  );
}

export default AddVehicle;
